<template>
  <section id="pricing" class="pricing-section">
    <!-- Background glows -->
    <div class="pr-glow pr-glow--cyan"></div>
    <div class="pr-glow pr-glow--blue"></div>

    <div class="container">
      <!-- Header -->
      <div class="pr-header">
        <span class="pr-badge wow fadeInUp" data-wow-delay=".1s">প্যাকেজ</span>
        <h2 class="wow fadeInUp" data-wow-delay=".2s">সুলভ মূল্য, সব সময়</h2>
        <p class="wow fadeInUp" data-wow-delay=".3s">সুলভ মূল্যে সেরা প্রস্তুতি নিশ্চিতে সাজানো আমাদের সব প্রিমিয়াম প্যাকেজ</p>
        <div class="pr-line wow fadeInUp" data-wow-delay=".4s"></div>
      </div>

      <!-- Carousel -->
      <carousel v-bind="settings" :breakpoints="breakpoints">
        <slide v-for="(pack, index) in packages" :key="index">
          <div class="pr-card wow fadeInUp" data-wow-delay=".2s">
            <!-- Card top accent -->
            <div class="pr-card-top" :class="{ 'has-tag': pack.tag }">
              <span
                  class="pr-tag"
                  v-if="pack.tag"
                  :style="pack.tag_color ? { background: pack.tag_color } : {}"
              >{{ pack.tag }}</span>
              <h4 class="pr-card-name">{{ pack.name }}</h4>
              <p class="pr-card-desc">{{ pack.short_description }}</p>
              <div class="pr-price-row">
                <span class="pr-price-actual">৳{{ englishToBengaliNumber(pack.actual_dam) }}</span>
                <span class="pr-price-old" v-if="pack.dam != pack.actual_dam">৳{{ englishToBengaliNumber(pack.dam) }}</span>
              </div>
            </div>

            <!-- Features -->
            <ul class="pr-features">
              <li v-for="(item, i) in getPackList(pack).slice(0, 7)" :key="i">
                <span class="pr-check">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="pr-feature-text">{{ item.replace('✓', '').trim() }}</span>
              </li>
            </ul>

            <!-- CTA -->
            <div class="pr-card-footer">
              <a @click="goToPackage(pack)" class="pr-btn">
                <span>বেছে নিন</span>
                <svg class="pr-btn-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.2673 4.20926C12.9674 3.92357 12.4926 3.93511 12.2069 4.23504C11.9213 4.53497 11.9328 5.0097 12.2327 5.29539L18.4841 11.25H3.75C3.33579 11.25 3 11.5858 3 12C3 12.4142 3.33579 12.75 3.75 12.75H18.4842L12.2327 18.7047C11.9328 18.9904 11.9213 19.4651 12.2069 19.7651C12.4926 20.065 12.9674 20.0765 13.2673 19.7908L20.6862 12.7241C20.8551 12.5632 20.9551 12.358 20.9861 12.1446C20.9952 12.0978 21 12.0495 21 12C21 11.9504 20.9952 11.902 20.986 11.8551C20.955 11.6419 20.855 11.4368 20.6862 11.276L13.2673 4.20926Z" fill="currentColor"/>
                </svg>
              </a>
            </div>
          </div>
        </slide>
        <template #addons>
          <Pagination />
        </template>
      </carousel>
    </div>
  </section>
</template>

<script>
import { defineComponent, onMounted, ref } from "vue";
import httpClient from "@/config/httpClient";
import { Carousel, Pagination, Slide } from 'vue3-carousel';
import { englishToBengaliNumber } from "@/config/utils";

export default defineComponent({
  name: "CoursePrice",
  components: { Carousel, Pagination, Slide },
  setup() {
    const packages = ref([]);

    onMounted(() => { getPackages(); });

    const getPackages = async () => {
      try {
        const response = await httpClient.get(process.env.VUE_APP_API_BASE_URL + "/packages", {
          params: { website: true },
        });
        if (response && response.package) {
          packages.value = response.package;
        }
      } catch (error) {
        console.error("Failed to fetch packages:", error);
      }
    };

    const getPackList = (pack) =>
      pack.long_description.split("\n").filter((item) => item.includes('✓'));

    const goToPackage = (pack) => {
      window.location.href = '/packages/' + pack.id;
    };

    return { packages, englishToBengaliNumber, getPackList, goToPackage };
  },
  data: () => ({
    settings: {
      itemsToShow: 1,
      autoplay: 4000,
      pauseAutoplayOnHover: true,
      loop: true,
      snapAlign: 'start',
      wrapAround: true,
    },
    breakpoints: {
      768: { itemsToShow: 1 },
      769: { itemsToShow: 3 },
      1024: { itemsToShow: 3 },
    },
  }),
});
</script>

<style scoped>
/* ── Section ── */
.pricing-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(140deg, #191b47 0%, #0d0f2e 55%, #0a1628 100%);
  padding: 80px 0 88px;
}

/* Glows */
.pr-glow {
  position: absolute; border-radius: 50%; pointer-events: none;
}
.pr-glow--cyan {
  top: -20%; left: -5%; width: 50vw; height: 50vw;
  background: radial-gradient(circle, rgba(56,182,255,0.12) 0%, transparent 70%);
}
.pr-glow--blue {
  bottom: -20%; right: -5%; width: 40vw; height: 40vw;
  background: radial-gradient(circle, rgba(0,149,255,0.12) 0%, transparent 70%);
}

/* Container */
.container {
  max-width: 1140px; margin: 0 auto; padding: 0 24px; position: relative;
}

/* Header */
.pr-header { text-align: center; margin-bottom: 52px; }
.pr-badge {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: "Noto Sans", sans-serif;
  font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #7CC6FF; background: rgba(0,149,255,0.14);
  border: 1px solid rgba(0,149,255,0.35);
  padding: 7px 18px; border-radius: 50px; margin-bottom: 16px;
}
.pr-header h2 {
  font-family: "Noto Sans", sans-serif;
  font-size: clamp(1.55rem, 3.5vw, 2.2rem); font-weight: 800;
  color: #fff; margin-bottom: 12px; line-height: 1.3; letter-spacing: -0.01em;
}
.pr-header p {
  font-family: "Noto Sans", sans-serif;
  font-size: 1rem; color: rgba(255,255,255,0.6);
  margin: 0 auto; max-width: 560px; line-height: 1.6;
}
.pr-line {
  width: 64px; height: 4px; border-radius: 4px; margin: 18px auto 0;
  background: linear-gradient(90deg, #0095FF, #38B6FF);
}

/* Card */
.pr-card {
  background: #fff;
  border-radius: 22px;
  margin: 8px auto 24px;
  display: flex; flex-direction: column;
  overflow: hidden;
  transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease;
  box-shadow: 0 8px 30px rgba(0,0,0,0.28);
  height: 100%;
  min-height: 500px;
  width: calc(100% - 24px);
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.06);
}
.pr-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 22px 54px rgba(0,149,255,0.28);
}

/* Card top */
.pr-card-top {
  padding: 26px 24px 22px;
  background: linear-gradient(135deg, #1a2d6b 0%, #191b47 100%);
  position: relative;
  overflow: hidden;
}
.pr-card-top::after {
  content: ""; position: absolute; top: -40px; right: -30px;
  width: 140px; height: 140px; border-radius: 50%;
  background: radial-gradient(circle, rgba(56,182,255,0.28) 0%, transparent 70%);
  pointer-events: none;
}

/* Promo tag (absolute → never affects card size) */
.pr-tag {
  position: absolute; top: 16px; right: 16px; z-index: 2;
  display: inline-flex; align-items: center;
  font-family: "Noto Sans", sans-serif;
  font-size: 0.66rem; font-weight: 800;
  letter-spacing: 0.03em;
  color: #16181f;
  background: linear-gradient(135deg, #38B6FF, #0095FF);
  padding: 4px 11px; border-radius: 50px;
  max-width: 130px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  box-shadow: 0 3px 10px rgba(0,0,0,0.28);
}

.pr-card-name {
  position: relative; z-index: 1;
  font-family: "Noto Sans", sans-serif;
  font-size: 1.12rem; font-weight: 800;
  color: #fff; margin: 0 0 8px; text-align: left;
  line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
  height: 3.14rem; /* exactly 2 lines → price rows align across cards */
}
.pr-card-top.has-tag .pr-card-name { padding-right: 76px; }

.pr-card-desc {
  position: relative; z-index: 1;
  font-family: "Noto Sans", sans-serif;
  font-size: 0.82rem; color: rgba(255,255,255,0.62);
  margin: 0 0 18px; text-align: left;
  line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.46rem; /* exactly 2 lines, fixed so 1- and 2-line descs align */
}
.pr-price-row {
  position: relative; z-index: 1;
  display: flex; align-items: baseline; gap: 10px;
}
.pr-price-old {
  font-size: 1rem; font-weight: 600;
  color: rgba(255,255,255,0.45);
  text-decoration: line-through;
  font-family: 'Noto Sans Bengali', 'Noto Sans', sans-serif;
}
.pr-price-actual {
  font-size: 2.1rem; font-weight: 900;
  color: #fff; line-height: 1;
  font-family: 'Noto Sans Bengali', 'Noto Sans', sans-serif;
  text-shadow: 0 2px 12px rgba(56,182,255,0.4);
}

/* Features */
.pr-features {
  list-style: none; padding: 22px 24px 8px; margin: 0; flex: 1;
}
.pr-features li {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 0.88rem; color: #374151;
  line-height: 1.55; margin-bottom: 11px;
  font-family: 'Noto Sans Bengali', 'Noto Sans', sans-serif;
  text-align: left;
}
.pr-feature-text { flex: 1; }
.pr-check {
  flex-shrink: 0; margin-top: 2px;
  width: 20px; height: 20px; border-radius: 50%;
  background: rgba(0,149,255,0.12);
  color: #0095ff;
  display: flex; align-items: center; justify-content: center;
}

/* Footer */
.pr-card-footer {
  padding: 12px 24px 26px;
}
.pr-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; text-align: center;
  background: linear-gradient(135deg, #38B6FF 0%, #0095FF 100%);
  color: #fff; font-weight: 800; font-size: 1rem;
  padding: 15px; border-radius: 14px;
  text-decoration: none; cursor: pointer;
  border: none;
  font-family: "Noto Sans", sans-serif;
  box-shadow: 0 8px 22px rgba(0,149,255,0.35), inset 0 1px 0 rgba(255,255,255,0.3);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.pr-btn-arrow { transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1); }
.pr-btn:hover {
  color: #fff;
  transform: translateY(-3px);
  box-shadow: 0 14px 30px rgba(0,149,255,0.5), inset 0 1px 0 rgba(255,255,255,0.3);
}
.pr-btn:hover .pr-btn-arrow { transform: translateX(5px); }

/* Make every slide stretch so cards share equal height */
:deep(.carousel__slide) { align-items: stretch; padding: 0; }
:deep(.carousel__track) { align-items: stretch; }

/* Carousel pagination — premium pill indicator */
:deep(.carousel__pagination) {
  margin: 28px 0 0; padding: 0;
  display: flex; justify-content: center; align-items: center; gap: 7px;
}
:deep(.carousel__pagination-button) {
  padding: 0; margin: 0; background: transparent; border: none;
}
:deep(.carousel__pagination-button::after) {
  content: ""; display: block;
  width: 8px; height: 8px; border-radius: 50px;
  background: rgba(255,255,255,0.25);
  transition: width 0.3s ease, background 0.3s ease;
}
:deep(.carousel__pagination-button:hover::after) {
  background: rgba(255,255,255,0.5);
}
:deep(.carousel__pagination-button--active::after) {
  width: 26px;
  background: linear-gradient(90deg, #38B6FF, #0095FF);
}

@media (max-width: 768px) {
  .pricing-section { padding: 56px 0 64px; }
  .pr-card { margin: 8px auto 20px; }
  .pr-header p { font-size: 0.9rem; max-width: 90%; }
}
</style>
